// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import 'myWorld/base/errors';
import 'myWorld/features/feature';

myw.DDFeature = myw.Feature.extend({
    /** Set to true in sub-classes to specify that the editor for these features should start in Popup mode
     * @type {boolean} */
    usePopupEditor: undefined,

    /** Override in sub-classes to specify a custom editor class to be used when editing features
     * @type {FeatureEditor} */
    editorClass: undefined,
    /** Override in sub-classes to specify a custom field editors for given fields.
     *  Keyed on field's internal name
     * @type {Object<FieldViewer>} */
    fieldEditors: {},
    /** Override in sub-classes to specify default values when creating a new feature <br/>
                Values specified here override ones specified in the database
     *  Keyed on field's internal name
     * @type {Object<string|number>} */
    defaults: {},

    /**
     * @class  Abstract class for a feature supported on data dictionary (DD) stored in myWorld <br/>
     *         A subclass holding the featureDD and other common properties will be generated for each feature type<br/>
     *         Use as super-class for feature models of external feature types
     *         Detached records should be created using database.createDetachedRecord()
     * @constructs
     * @param {featureData}     featureData Feature details
     * @augments Feature
     */
    initialize(featureData, options) {
        // Call the super constructor
        myw.Feature.prototype.initialize.call(this, featureData);

        /** Self's DD information
         * @memberof Feature.prototype
         * @member {featureDD} featureDD */

        /** Whether this is feature is new (detached from database) or not
         * @type {Boolean} */
        this.isNew = !featureData;

        this._loadedAspects = {};

        this._loadedAspects['simple'] = true;
        this._loadedAspects['display_values'] = true;
        this._loadedAspects['lobs'] = true;
        this._loadedAspects['calculated'] = false;

        if (!featureData) {
            //detached feature
            this.id = null;

            //set default values
            //defaults defined in feature's model class will override the ones defined in DD
            _.extend(this.properties, this.featureDD.defaults, _.result(this, 'defaults'));
        }
    },

    /**
     * Returns the name of self's geometry field that has a value in a specific world
     * @param {string}  worldName   Name of the world of the desired geometry
     * @return {string}  Name of geometry field
     */
    getGeometryFieldNameInWorld(worldName) {
        const geom = this.geometry;
        const geomWorldName = (geom && geom.world_name) || 'geo';

        // Primary geometry is the one we are after.
        if (geom && geomWorldName == worldName) {
            return this.featureDD.primary_geom_name;
        } else {
            const fieldName = _.find(
                _.keys(this.secondary_geometries),
                k =>
                    this.secondary_geometries[k] &&
                    this.secondary_geometries[k].world_name == worldName
            );
            return fieldName;
        }
    },

    /**
     * Returns the names of self's geometry fields that has a value in a specific world
     * @param {string}  worldName   Name of the world of the desired geometry
     * @return {array<string>}  Names of geometry field
     */
    getGeometryFieldNamesInWorld(worldName) {
        let fieldNames = [];
        const geom = this.geometry,
            geomWorldName = (geom && geom.world_name) || 'geo';

        if (geom && geomWorldName == worldName) {
            fieldNames.push(this.featureDD.primary_geom_name);
        }

        if (this.secondary_geometries) {
            fieldNames = fieldNames.concat(_.keys(this.secondary_geometries));
        }

        return fieldNames;
    },

    /**
     * Ensures properties for the specified aspects are available.
     * Supported aspects are: <br/>
     *     'simple':            stored properties of the feature, excluding lobs <br/>
     *     'display_values':    additional values to be used for displaying to the user <br/>
     *     'lobs'               large object properties. Currently only properties of type 'image'
     * @param  {string|string[]}    aspects  Aspect(s) to ensure
     * @param  {boolean}    [reload=false]   If true, details are re-obtained from server
     * @return {Promise}    Promise that is resolved when the feature data as been obtained
     */
    ensure(aspects, reload) {
        if (this.isNew) return Promise.resolve();

        if (this.featureDD.unauthorized) return Promise.reject(new myw.UnauthorizedError());

        if (_.isString(aspects)) aspects = [aspects];

        const loadedAspects = this._loadedAspects;
        const missingAspects = _.filter(aspects, prop => !loadedAspects[prop]);

        if (missingAspects.length || reload) {
            return this.getDetails(reload, missingAspects);
        } else {
            // nothing missing
            return Promise.resolve(this);
        }
    },

    /**
     * Loads/reloads all of the properties of this feature from the server.
     * @param  {boolean}    [reload=false]      If there are details available, whether to obtain new details or not
     * @param  {string[]}   [missingAspects]    Aspects that are missing and should be obtained
     * @return {promise} Resolved if/when the feature properties have been updated
     */
    getDetails(reload, missingAspects) {
        const //aspects missing even when considering a possible pending request
            reallyMissing = _.difference(missingAspects, this._expectedAspects),
            //true means the missing aspects will be received with the pending request
            onTheWay = this._getDetailsPromise && _.isEmpty(reallyMissing),
            needMoreProperties = !onTheWay;

        if (reload || needMoreProperties) {
            let promise;

            const includeLobs =
                _.contains(missingAspects, 'lobs') || (reload && this._loadedAspects['lobs']);

            this._expectedAspects = missingAspects;

            if (reload || _.without(missingAspects, 'calculated').length > 0) {
                //there are aspects we need to get from the database
                promise = this.datasource
                    .getFeatureByUrn(this.getUrn(), includeLobs, this.getDelta())
                    .then(this.mergeFeatureDetails.bind(this, includeLobs));
            } else {
                //we only need the calculated properties
                promise = Promise.resolve();
            }

            if (reload || _.contains(missingAspects, 'calculated')) {
                promise = promise //make sure we first got the properties from the db, before doing the calculations
                    .then(this._getCalculatedValues.bind(this))
                    .then(() => {
                        this._loadedAspects['calculated'] = true;
                        return this;
                    });
            }

            this._getDetailsPromise = promise;
        }
        return this._getDetailsPromise;
    },

    /**
     * properties obtained from the database and updates self with them
     * @param  {boolean} includeLobs
     * @return {Promise}
     * @protected
     */
    mergeFeatureDetails(includeLobs, feature) {
        if (!feature) return this; //handles external DSs not providing individual details (in addition to what is obtained from selection requests)
        if (!this.geometry && feature.geometry) this.geometry = {};

        _.extendOwn(this.properties, feature.properties);
        _.extendOwn(this.geometry, feature.geometry);

        this.displayValues = feature.displayValues;

        if (feature.secondary_geometries) {
            this.secondary_geometries = {};
            _.extendOwn(this.secondary_geometries, feature.secondary_geometries);
        }

        this._loadedAspects['simple'] = true;
        this._loadedAspects['display_values'] = true;
        //for optional properties we need to consider previous value for reload requests and "simultaneous" requests of different property types
        this._loadedAspects['lobs'] = this._loadedAspects['lobs'] || includeLobs;

        this._expectedAspects = null;

        return this;
    },

    /**
     * Get (and stores on self) the values for the calculated properties
     * @return {Promise} Promise to be resolved when all the properties have been set on self
     * @private
     */
    _getCalculatedValues() {
        const requests = [];
        const setProperty = function(fieldName, value) {
            this.properties[fieldName] = value;
        };
        const errorHandler = function(fieldName, reason) {
            this.displayValues[fieldName] = reason;
        };

        for (const fieldName in this.featureDD.fields) {
            const fieldDD = this.featureDD.fields[fieldName];

            //reference_set field are only calculated on-demand
            if (
                fieldDD.value &&
                fieldDD.value.indexOf('method(') === 0 &&
                fieldDD.type != 'reference_set'
            ) {
                const methodName = fieldDD.value.slice(7).split(')')[0];
                let result;
                if (!this[methodName]) {
                    this.displayValues[fieldName] = new myw.MissingImplementationError(
                        `Expected '${this.getUrn()} to respond to '${methodName}'`
                    );
                    console.log(
                        `***Error: Expected '${this.getUrn()} to respond to '${methodName}'`
                    );
                    continue;
                }

                try {
                    result = this[methodName]();
                } catch (e) {
                    result = undefined;
                    console.log(`Error executing method '${methodName}' on ${this.getUrn()}: `, e);
                    this.displayValues[fieldName] = e;
                }
                requests.push(
                    Promise.resolve(result)
                        .then(setProperty.bind(this, fieldName))
                        .catch(errorHandler.bind(this, fieldName))
                );
            }
        }
        return Promise.all(requests);
    },

    /**
     * Returns the title for self<br/>
     * Overridden to use expression specified in Data Dictionary
     * @return {string}
     */
    getTitle() {
        return this._evaluateFieldExpression(this.featureDD.title_expr, `${this.type}.title`);
    },

    /**
     * Returns short descritption for self <br/>
     * Overridden to use expression specified in Data Dictionary
     * @return {string}
     */
    getShortDescription() {
        return this._evaluateFieldExpression(this.featureDD.short_description_expr);
    },

    /**
     * External name of self's feature type
     * @return {string}
     */
    getTypeExternalName() {
        return this.featureDD.external_name;
    },

    /**
     * Returns Field group information or undefined if none is defined...
     * @return {Array<fieldGroup>}
     */
    getFieldGroups() {
        const fieldGroups = this.featureDD.field_groups;
        if (fieldGroups && fieldGroups.length > 0) return fieldGroups;
    },

    /**
     * Returns the order in which fields should be presented to the user. If no field groups defined then explicitly exclude
     * internal fields including world name fields and geometry fields.
     * @return {Array<string>}
     */
    getFieldsOrder() {
        const fieldGroups = this.getFieldGroups();
        if (fieldGroups) {
            return _.chain(fieldGroups)
                .pluck('fields')
                .flatten()
                .pluck('field_name')
                .value();
        } else {
            const fieldsOrder = this.featureDD.fields_order.length ? this.featureDD.fields_order : Object.keys(this.properties);
            return _.reject(
                _.difference(fieldsOrder, this._excludedFields),
                value => {
                    const isGeom = _.contains(
                        ['point', 'linestring', 'polygon', 'raster'],
                        this.getFieldDD(value).type
                    );
                    return value.match(/myw_gwn_.*|myw_orientation_.*/) || isGeom;
                }
            );
        }
    },

    getFieldDD(internalName) {
        return this.featureDD.fields[internalName] || myw.Feature.prototype.getFieldDD.call(this,internalName);
    },

    getFieldsDD() {
        const fieldsDD = this.featureDD.fields;
        if (fieldsDD) return fieldsDD;
        else {
            //fields is missing - DD needs to be updated from external server?
            const fieldNames = _.keys(this.properties);
            return _.object(fieldNames, _.map(fieldNames, this.getFieldDD));
        }
    },

    /**
     * Feature is editable if
     * 1. is in list of editable features in app
     * 2. is updateable from gui
     * 3. isn't from delta schema request (ex: from a forward view layer request)
     */
    isEditable() {
        const isEditableTypeInApp = Object.keys(this.datasource.appEditableFeatureTypes).includes(
            this.featureDD.name
        );
        return isEditableTypeInApp && this.featureDD.update_from_gui && !this.isDeltaSchema();
    },

    /**
     * Gets the dd for the primary geometry of the feature
     * @return {object}
     */
    primaryGeomFieldDD() {
        return this.featureDD.fields[this.featureDD.primary_geom_name];
    },

    /**
     * @return {string} Geometry type of this feature from the DD mapped to GeoJSON geometry type. <br/>
     *                  Returns undefined if the DD doesn't specify a geometry type
     */
    getDDGeometryType() {
        return myw.Feature.geomMapTable[this.featureDD.geometry_type];
    }
});

export default myw.DDFeature;
